import { History, HistoryIcon } from 'components/icons';
import DynamicHistoryModal, { QueryAndMutationProps } from 'components/modals/history/DynamicHistoryModal';
import HistoryModal, { HistorySheet } from 'components/modals/history/HistoryModal';
import { HISTORY_SHEET } from 'components/modals/sideSheet';
import { useModal } from 'components/modals/useModal';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FC } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { CallForActionButton, CallForActionButtonTooltip } from './CallForActionButton';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    width: '750px',
};

type BaseProps = {
    entityType: string;
    entityId: string;
    fieldType: string;
    fieldName: string;
    activity: string;
    /** Opens the history as a side sheet beside the record, as the new design's invoice does, instead of a dialog. */
    detached?: HistorySheet;
    /** A plain glyph in the link colour, without the tinted circle: beside a label that carries other icons. */
    plain?: boolean;
};

type WithQueryAndMutation = BaseProps & QueryAndMutationProps;

type WithoutQuery = BaseProps & {
    query?: undefined;
    updateMutation?: undefined;
    deleteMutation?: undefined;
};

type Props = WithQueryAndMutation | WithoutQuery;

const DisplayHistoryButton: FC<Props> = ({
    entityType,
    entityId,
    fieldType,
    fieldName,
    activity,
    query,
    updateMutation,
    deleteMutation,
    detached,
    plain = false,
}) => {
    const { openModal, closeModal } = useModal();
    const isNewDesign = useIsNewDesign();

    const hasReadEntityFieldHistoryPermission = useHasPermissions(['READ.ENTITY_FIELD_HISTORY']);

    const displayHistory = () => {
        const sharedProps = {
            entityType,
            entityId,
            fieldType,
            fieldName,
            activity,
            onClose: closeModal,
        };
        openModal(
            query === undefined ? (
                <HistoryModal {...sharedProps} detached={detached} />
            ) : (
                <DynamicHistoryModal
                    {...sharedProps}
                    query={query}
                    updateMutation={updateMutation}
                    deleteMutation={deleteMutation}
                />
            ),
            closeModal,
            detached ? undefined : FORM_OVERFLOW_CUSTOM_STYLE,
            undefined,
            detached ? HISTORY_SHEET : undefined
        );
    };

    if (!hasReadEntityFieldHistoryPermission) {
        return null;
    }

    return (
        <CallForActionButtonTooltip title={t('history.button-tooltip', { fieldName, activity })}>
            <CallForActionButton
                variant="contained"
                shape="rounded"
                color="info"
                className={cn(
                    'size-6',
                    plain && 'text-primary hover:bg-muted hover:text-primary rounded-md bg-transparent'
                )}
                aria-label={t('history.button-tooltip', { fieldName, activity })}
                onClick={displayHistory}
            >
                {/* Beside a regular 14px name: 16px, at the 1.5px stroke the kit button would otherwise make 2px. */}
                {isNewDesign ? <HistoryIcon className="size-4 stroke-[1.5px]!" /> : <History />}
            </CallForActionButton>
        </CallForActionButtonTooltip>
    );
};

export default DisplayHistoryButton;
